import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { useDataHora } from "../../../core/hooks/useDataHora";
import { useFetchClientesPorPrograma } from "../../hooks/useFetchClientesPorPrograma";
import { useFetchProgramasFidelidade } from "../../hooks/useFetchProgramasFidelidade";
import { IClienteProgramaFidelidade } from "../../interface/IClienteProgramaFidelidade";
import { IProgramaFidelidade } from "../../interface/IProgramaFidelidade";
import {
    ButtonsContainer,
    EmptyState,
    FiltrosContainer,
    Loading,
    PageContainer,
    TableContainer,
} from "./styles";

const labelPrograma = (p: IProgramaFidelidade) => {
    if (p.tipo === "CASHBACK") {
        return `Cashback ${p.porcentagemRetorno ?? 0}%${p.limiteAcumuloCashback ? ` (limite R$ ${Number(p.limiteAcumuloCashback).toFixed(2)})` : ""}`;
    }
    return `Itens — ${p.quantidadePedidosNecessarios ?? 0} pedidos`;
};

const formatarSaldo = (item: IClienteProgramaFidelidade, tipo?: string) => {
    if (tipo === "CASHBACK") {
        return `R$ ${(item.saldoReais ?? 0).toFixed(2).replace(".", ",")}`;
    }
    return `${item.saldoPedidos ?? 0} pedido${item.saldoPedidos !== 1 ? "s" : ""}`;
};

export const ProgramasFidelidade = () => {
    const navigate = useNavigate();
    const { verificarAcessoFuncionalidade, perfil } = useAutenticacaoContext();
    const { dataBr } = useDataHora();

    useEffect(() => {
        if (perfil && !verificarAcessoFuncionalidade("Gestão de clientes")) {
            navigate("/nao-autorizado");
        }
    }, [perfil]);

    const { programas, isLoading: loadingProgramas } = useFetchProgramasFidelidade();
    const {
        dados,
        isLoading,
        programaUuid,
        paginaAtual,
        nome,
        telefone,
        setNome,
        setTelefone,
        handlePaginaChange,
        handleFiltrar,
        handleLimpar,
        handleProgramaChange,
        exportar,
        loadingExportar,
    } = useFetchClientesPorPrograma();

    const programaSelecionado = programas?.find((p) => p.uuid === programaUuid);

    return (
        <PageContainer>
            <FiltrosContainer>
                <Typography variant="h6">Programas de Fidelidade</Typography>
                <Box className="filtros-row">
                    <FormControl size="small" className="select-programa">
                        <InputLabel>Programa</InputLabel>
                        <Select
                            label="Programa"
                            value={programaUuid ?? ""}
                            onChange={(e) => handleProgramaChange(e.target.value || null)}
                            disabled={loadingProgramas}
                        >
                            <MenuItem value="">
                                <em>Selecione um programa</em>
                            </MenuItem>
                            {programas?.map((p) => (
                                <MenuItem key={p.uuid} value={p.uuid}>
                                    {labelPrograma(p)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        placeholder="Nome"
                        size="small"
                        className="input-filtro"
                        value={nome}
                        onChange={(e) => setNome(e.target.value)}
                        disabled={!programaUuid}
                    />
                    <TextField
                        placeholder="Telefone"
                        size="small"
                        className="input-filtro"
                        value={telefone}
                        onChange={(e) => setTelefone(e.target.value)}
                        disabled={!programaUuid}
                    />
                    <ButtonsContainer>
                        <Button variant="outlined" onClick={handleLimpar} disabled={!programaUuid}>
                            Limpar
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<FilterListIcon />}
                            onClick={handleFiltrar}
                            disabled={!programaUuid}
                        >
                            Filtrar
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={loadingExportar ? <CircularProgress size={16} /> : <DownloadIcon />}
                            onClick={exportar}
                            disabled={!programaUuid || !dados?.itens?.length || loadingExportar}
                        >
                            Exportar XLS
                        </Button>
                    </ButtonsContainer>
                </Box>
            </FiltrosContainer>

            <Paper
                sx={{
                    padding: "1rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    margin: "0 auto",
                    width: "85%",
                }}
            >
                <TableContainer>
                    {!programaUuid ? (
                        <EmptyState>
                            <Typography color="text.secondary">Selecione um programa de fidelidade para visualizar os clientes</Typography>
                        </EmptyState>
                    ) : isLoading ? (
                        <Loading>
                            <CircularProgress />
                        </Loading>
                    ) : dados && dados.itens?.length ? (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align="left" width="22%">Nome</TableCell>
                                    <TableCell align="left" width="18%">Telefone</TableCell>
                                    <TableCell align="left" width="15%">Saldo</TableCell>
                                    <TableCell align="left" width="12%">Pedidos acum.</TableCell>
                                    <TableCell align="left" width="17%">Vinculado em</TableCell>
                                    <TableCell align="left" width="16%">Última atualização</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dados.itens.map((item, idx) => (
                                    <TableRow key={item.clienteUuid ?? idx} hover>
                                        <TableCell>{item.nome ?? "—"}</TableCell>
                                        <TableCell>{item.telefone ?? "—"}</TableCell>
                                        <TableCell>{formatarSaldo(item, programaSelecionado?.tipo)}</TableCell>
                                        <TableCell>{item.saldoPedidos ?? 0}</TableCell>
                                        <TableCell>
                                            {item.vinculadoAs ? dataBr(String(item.vinculadoAs)) : "—"}
                                        </TableCell>
                                        <TableCell>
                                            {item.ultimaAtualizacaoAs ? dataBr(String(item.ultimaAtualizacaoAs)) : "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <EmptyState>
                            <Typography>Nenhum cliente encontrado para este programa</Typography>
                        </EmptyState>
                    )}
                </TableContainer>

                {dados && dados.totalPaginas > 1 && (
                    <Pagination
                        count={dados.totalPaginas}
                        page={paginaAtual}
                        onChange={handlePaginaChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                        siblingCount={2}
                        boundaryCount={2}
                        sx={{ marginTop: "1rem" }}
                    />
                )}
            </Paper>
        </PageContainer>
    );
};
